/**
 * Звідки брати сегменти для кліпу — легка половина нарізки (без mediabunny).
 *
 * Живе окремо від utils/clip.js, бо її кличуть плеєр серії і сторінка ефіру
 * на кожне відкриття, а рушій нарізки (mediabunny) має вантажитись тільки
 * тоді, коли хтось справді відкрив редактор кліпу.
 */

export const CLIP_MIN = 2
// До трьох хвилин: довше — це вже не момент, а шматок серії
export const CLIP_MAX = 180

export class ClipError extends Error {
  constructor(message, code = 'failed') {
    super(message)
    this.name = 'ClipError'
    this.code = code
  }
}

// --- Звідки брати сегменти ---

/**
 * Сегменти з живого hls.js: рівно той рівень якості, що зараз грає, у тій
 * самій шкалі часу, що й video.currentTime. Для прямого ефіру інакше й не
 * можна — власний розбір плейлиста нічого не знав би про зсув, який hls.js
 * накопичив від початку перегляду
 *
 * @return {null | {url:string, start:number, duration:number, init:string|null}[]}
 */
export const fragmentsFromHls = (hls) => {
  if (!hls?.levels?.length) return null
  const index = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel
  // Глядач слухає «тільки звук» — кліп з нього був би без картинки. Тоді
  // хай fragmentsFromPlaylist візьме найкращий рівень з відео з мастера
  if (!hls.levels[index]?.height) return null
  const details = hls.levels[index]?.details
  if (!details?.fragments?.length) return null

  return details.fragments.map((frag) => {
    const method = frag.decryptdata?.method
    return {
      url: frag.url,
      start: frag.start,
      duration: frag.duration,
      init: frag.initSegment?.url || null,
      encrypted: !!method && method !== 'NONE'
    }
  })
}

const attr = (line, name) => {
  const match = line.match(new RegExp(`${name}=("[^"]*"|[^,]*)`))
  return match ? match[1].replace(/^"|"$/g, '') : null
}

/**
 * Сегменти з плейлиста запису — коли hls.js немає (Safari грає HLS сам).
 * Для запису шкала проста: від нуля, сегмент за сегментом
 *
 * @param {string} url плейлист серії (мастер або одного рівня)
 * @param {(url:string) => string} [sign] дописати токен до кожної адреси
 */
export const fragmentsFromPlaylist = async (url, sign = (u) => u) => {
  const load = async (target) => {
    const res = await fetch(sign(target))
    if (!res.ok) throw new ClipError(`Плейлист не відкрився (${res.status})`)
    return res.text()
  }

  let playlistUrl = url
  let text = await load(url)

  // Мастер — беремо найкращий рівень із відео (не «тільки звук»)
  if (text.includes('#EXT-X-STREAM-INF')) {
    const lines = text.split('\n')
    let best = null
    lines.forEach((line, i) => {
      if (!line.startsWith('#EXT-X-STREAM-INF')) return
      const bandwidth = Number(attr(line, 'BANDWIDTH')) || 0
      const resolution = attr(line, 'RESOLUTION')
      const height = resolution ? Number(resolution.split('x')[1]) : 0
      const next = lines.slice(i + 1).find((l) => l.trim() && !l.startsWith('#'))
      if (!next || !resolution) return
      // Вище за 1080p кліпу не треба — важитиме вдвічі більше заради пікселів,
      // яких у колонці сайту однаково не видно
      if (height > 1080) return
      if (!best || bandwidth > best.bandwidth) best = { bandwidth, uri: next.trim() }
    })
    if (!best) throw new ClipError('У плейлисті немає відео')
    playlistUrl = new URL(best.uri, playlistUrl).toString()
    text = await load(playlistUrl)
  }

  const fragments = []
  let start = 0
  let duration = 0
  let init = null

  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('#EXT-X-KEY') && !/METHOD=NONE/.test(line)) {
      throw new ClipError('Цей запис зашифрований — кліп з нього не зробити', 'encrypted')
    }
    if (line.startsWith('#EXT-X-MAP')) {
      const uri = attr(line, 'URI')
      if (uri) init = new URL(uri, playlistUrl).toString()
      continue
    }
    if (line.startsWith('#EXTINF:')) {
      duration = parseFloat(line.slice(8)) || 0
      continue
    }
    if (line.startsWith('#')) continue

    fragments.push({ url: new URL(line, playlistUrl).toString(), start, duration, init, encrypted: false })
    start += duration
  }

  return fragments
}

